import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, fmtUsd, fmtSigned, PORTFOLIO_KIND_IDS, BASKET_PORTFOLIO_ENABLED } from "@/lib/journal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from "react-i18next";
import { Trash, Plus } from "@phosphor-icons/react";

const KINDS = BASKET_PORTFOLIO_ENABLED ? [...PORTFOLIO_KIND_IDS, "basket"] : PORTFOLIO_KIND_IDS;

export const PortfolioBar = ({ portfolios, activeId, setActiveId, onChanged }) => {
  const { t } = useTranslation();
  const [manageOpen, setManageOpen] = useState(false);
  const [form, setForm] = useState({ name: "", kind: "onchain", lp_symbol: "" });
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Portfolio name is required");
    setSaving(true);
    try {
      await api.post("/portfolios", {
        name: form.name.trim(),
        kind: form.kind,
        ...(form.kind === "basket" ? { lp_symbol: form.lp_symbol.trim() || "LP" } : {}),
      });
      toast.success("Portfolio created");
      setForm({ name: "", kind: "onchain", lp_symbol: "" });
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create portfolio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      const { data } = await api.delete(`/portfolios/${p.id}`);
      toast.success(`Removed "${p.name}" (${data.trades_deleted} trades)`);
      if (activeId === p.id) setActiveId("all");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete portfolio");
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="portfolio-bar">
      <button
        data-testid="portfolio-tab-all"
        onClick={() => setActiveId("all")}
        className={`border px-3 py-1.5 text-xs uppercase tracking-[0.15em] transition-colors duration-200 ${
          activeId === "all"
            ? "border-[#C58361] bg-[#C58361]/10 text-[#C58361]"
            : "border-[#1F1F1F] text-zinc-500 hover:text-zinc-200"
        }`}
      >
        All Portfolios
      </button>
      {portfolios.map((p) => (
        <button
          key={p.id}
          data-testid={`portfolio-tab-${p.id}`}
          onClick={() => setActiveId(p.id)}
          className={`border px-3 py-1.5 text-xs tracking-wide transition-colors duration-200 ${
            activeId === p.id
              ? "border-[#C58361] bg-[#C58361]/10 text-[#C58361]"
              : "border-[#1F1F1F] text-zinc-500 hover:text-zinc-200"
          }`}
        >
          {p.name}
          <span className="ml-2 font-mono text-[10px] opacity-60">{p.trade_count}</span>
        </button>
      ))}
      <Button
        data-testid="manage-portfolios-btn"
        onClick={() => setManageOpen(true)}
        className="h-[30px] rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-zinc-400 hover:bg-[#111111] hover:text-white"
      >
        <Plus size={12} className="mr-1.5" /> Portfolio
      </Button>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent data-testid="portfolio-dialog" className="max-h-[90vh] overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-medium tracking-tight">
              Portfolios <span className="text-[#C58361]">/ Sources</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              Track wallets, exchange and broker accounts as independent sources of trades.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 pt-2">
            <div className="divide-y divide-[#1F1F1F] border border-[#1F1F1F]">
              {portfolios.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-4 py-3" data-testid={`portfolio-item-${p.id}`}>
                  <div>
                    <p className="text-sm text-white">
                      {p.name}
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-600">{p.kind}</span>
                    </p>
                    <p className="mt-1 font-mono text-[11px] text-zinc-500">
                      {p.trade_count} trades · {fmtUsd(p.cost_basis)} basis · {fmtSigned(p.realized_pnl)}
                    </p>
                    {p.address && (
                      <p className="mt-1 font-mono text-[10px] text-zinc-600" data-testid={`portfolio-sync-${p.id}`}>
                        auto-sync {p.last_sync_at ? `last ran ${p.last_sync_at.slice(0, 16).replace("T", " ")} UTC` : "pending"}
                        {p.last_sync_imported ? ` · +${p.last_sync_imported} swaps` : ""}
                        {p.last_sync_error ? " · last run had an error" : ""}
                      </p>
                    )}
                  </div>
                  <Button
                    data-testid={`portfolio-delete-${p.id}`}
                    onClick={() => remove(p)}
                    className="h-8 w-8 rounded-none border border-[#1F1F1F] bg-transparent p-0 text-zinc-500 hover:bg-[#EF4444]/10 hover:text-[#EF4444]"
                  >
                    <Trash size={13} />
                  </Button>
                </div>
              ))}
            </div>

            <div className="space-y-4 border border-[#1F1F1F] p-4">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">New Portfolio</p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Name</Label>
                <Input
                  data-testid="portfolio-name-input"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && create()}
                  placeholder="Base Wallet / IBKR Account"
                  className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-[#C58361]"
                />
                <p className="font-mono text-[10px] text-zinc-600">
                  Link a wallet later with Import Wallet — the address and network are saved automatically.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("portfolio.kind")}</Label>
                <Select value={form.kind} onValueChange={(v) => setForm((p) => ({ ...p, kind: v }))}>
                  <SelectTrigger data-testid="portfolio-kind-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                    {KINDS.map((k) => (
                      <SelectItem key={k} value={k} data-testid={`portfolio-kind-${k}`}>
                        {t(`portfolioKinds.${k}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.kind === "basket" && (
                  <>
                    <Input
                      data-testid="portfolio-lp-symbol-input"
                      value={form.lp_symbol}
                      onChange={(e) => setForm((p) => ({ ...p, lp_symbol: e.target.value }))}
                      placeholder="ALVO13-LP"
                      className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-[#C58361]"
                    />
                    <p className="font-mono text-[10px] text-zinc-600">{t("portfolio.basketHint")}</p>
                  </>
                )}
              </div>
              <Button
                data-testid="portfolio-create-btn"
                onClick={create}
                disabled={saving}
                className="w-full rounded-none bg-[#C58361] py-5 font-medium text-black hover:bg-[#A86B4C]"
              >
                {saving ? "Creating…" : "Create Portfolio"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
